<template>
  <v-overlay v-if="!termConditionsChecked">
    <v-progress-circular
      indeterminate
      size="64">
    </v-progress-circular>
  </v-overlay>
  <v-app v-else>
    <v-app-bar app color="main" dark>
      <v-app-bar-nav-icon @click.stop="drawer = !drawer" class="mx-auto"/>
      <router-link :to="{ path: '/'}">
          <v-img v-if="tenantsWithLogos.includes(`${$auth.tenant}`)"
            max-height="110"
            max-width="110"
            class="ml-6"
            :src="require(`@/assets/${$auth.tenant}_logo.png`)"
            :alt="`${$auth.tenant} Logo`">
          </v-img>
          <v-app-bar-nav-icon v-else>
            <v-icon color="#fff">mdi-home</v-icon>
          </v-app-bar-nav-icon>
        </router-link>
      <v-spacer></v-spacer>
      <VideoMenu v-if="$auth.tenant === 'awg'" />
      <v-btn v-if="showCreditUpload" color="primary" @click.stop="openCreditFormModal">
        Submit Credit Request
        <v-icon right dark>mdi-upload</v-icon>
      </v-btn>
      <v-col cols="auto" class="pr-0">
        <PortalNavMenu active="partner" />
      </v-col>
    </v-app-bar>
    <v-navigation-drawer
      v-model="drawer"
      app
      color="toolbar">
      <v-list nav>
        <!-- View Mode Selection for ARG tenant -->
        <v-list-group v-if="$auth.tenant === 'alliance-retail-group'" prepend-icon="mdi-file-search-outline" no-action>
          <template v-slot:activator>
            <v-list-item-content>
              <v-list-item-title>Files</v-list-item-title>
            </v-list-item-content>
          </template>
          
          <v-list-item @click="handleFilesViewClick(true)">
            <v-list-item-action>
              <v-icon>mdi-table</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>Table View</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          
          <v-list-item @click="handleFilesViewClick(false)">
              <v-list-item-action>
              <v-icon>mdi-folder-outline</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>Explorer View</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-group>
        
        <!-- Simple Files link for all other tenants -->
        <v-list-item v-else :to="{ name: 'Current'}" exact link>
          <v-list-item-action>
            <v-icon>mdi-file-search-outline</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>Files</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        
        <!-- Default View Preference - only for ARG tenant -->
        <v-list-item v-if="$auth.tenant === 'alliance-retail-group'">
          <v-list-item-action>
            <v-icon>mdi-cog</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title class="text-caption">Default View</v-list-item-title>
            <v-list-item-subtitle>
              <v-select
                v-model="preferredFileView"
                :items="viewOptions"
                dense
                hide-details
                solo
                flat
                @change="setDefaultView"
                style="margin-top: 4px;"
              ></v-select>
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        
        <v-list-item v-if="hasDocumentUploadAccess"
          :to="{ name: 'Upload'}" exact link>
          <v-list-item-action>
            <v-icon>mdi-file-upload-outline</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>Upload</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item v-if="hasOrderGridAccess" :to="{ name: 'Retail Ad Manager'}" exact link>
          <v-list-item-action>
            <v-icon>mdi-cart-outline</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>
              {{ $auth.tenant === 'alliance-retail-group' ? 'Order Grid' : 'Retail Ad Manager' }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item v-if="showAdPlanEdit" :to="{ name: 'AdPlannerEdit'}" exact link>
          <v-list-item-action>
            <v-icon>mdi-pencil</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>
              Ad Planner SRP Edit
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item :to="{ name: 'AdPlannerQuery'}" exact link>
          <v-list-item-action>
            <v-icon>mdi-table-search</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>
              Ad Planner Query
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item v-if="hasNotificationsAccess" :to="{ name: 'Notifications'}" exact link>
          <v-list-item-action>
            <v-icon>mdi-bell-outline</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>
              Notifications
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item v-if="tprSearchVisible" :href="tprSearchLink" link>
          <v-list-item-action>
            <v-icon>mdi-magnify</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>TPR Search</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item v-if="finSearchVisible" :href="finSearchLink" link>
          <v-list-item-action>
            <v-icon>mdi-magnify</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>Financials Search</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
      <template v-slot:append>
        <v-divider></v-divider>
        <div :class="[(!miniVariant) ? 'pa-2': 'pa-1']" style="height:64px;">
          <v-btn block id="logoutBtn" :style="styleObject" class="mt-2" @click.prevent="logout()">
            <v-icon color="#fff">mdi-logout</v-icon>
            <span v-if="!miniVariant">Logout</span>
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <Maintenance v-if="maintenanceStatus" />

    <v-main v-else style="background-color: #F2F2F2">
      <alertbar></alertbar>
      <router-view v-if="$auth.isAuthenticated && hasPortalAccess" />
      <v-alert v-else type="error" text border="left">
        You don't have authorized permissions to access this site.
      </v-alert>
    </v-main>
    <v-footer color="main" app class="justify-space-between">
      <span class="white--text"> iPro <span class="registeredTrademark">&reg;</span> Systems, LLC. &copy; {{ new Date().getFullYear() }} </span>
      <span class="blue--text text--lighten-2">v. {{version}}</span>
    </v-footer>
    <CreditUploadDialog
      v-if="showCreditFormModal"
      :value.sync="showCreditFormModal"
      :stores="userStores"
      :isJBGRetailer="isJBGRetailer"
      @close="() => {showCreditFormModal = false}"
      >
    </CreditUploadDialog>
    <TermsAndConditionsModal
      v-if="showTermsAndConditionsModal"
      v-model="showTermsAndConditionsModal"
      @update="updateTC"
      @logout="logout">
    </TermsAndConditionsModal>
  </v-app>
</template>
<script lang="js">
// API
import PartyAttributeValue from '@/axios/party-attribute-value-endpoint'
import Party from '@/axios/party'

// components
const PortalNavMenu = () => import('@/components/shared/PortalNavMenu.vue')
import alertbar from '@/components/shared/alertbar.vue'
import CreditUploadDialog from '@/components/dialogs/CreditUploadDialog.vue'
import TermsAndConditionsModal from '@/components/TermsAndConditionsModal.vue'

// mixins
import { userAccess } from '@/mixins/user-access'
import { displayAlert } from '@/mixins/alert'
// api
const VideoMenu = () => import('@/components/shared/VideoMenu.vue')
const Maintenance = () => import('@/components/shared/Maintenance.vue')
export default {
  name: 'App',
  components: { alertbar, Maintenance, VideoMenu, PortalNavMenu, CreditUploadDialog, TermsAndConditionsModal },
  data () {
    return {
      drawer: false,
      miniVariant: false,
      showCreditFormModal: false,
      loading: false,
      file: null,
      styleObject: {
        background: '#E53935',
        color: 'white',
        fontSize: '15px'
      },
      preferredFileView: 'table',
      viewOptions: [
        { text: 'Table View', value: 'table' },
        { text: 'Explorer View', value: 'explorer' }
      ],
      showTermsAndConditionsModal: false,
      termConditionsChecked: false,
      userAttributes: [],
      tprSearchRoles: new Set(["arg-owner-retailer", "arg-store-retailer", "arg-vendor", "arg-group-manager",
        "ipro-admin", "arg-admin", "arg-support", "arg-category-manager", "arg-accounting"]),
      finSearchRoles: new Set(["arg-owner-retailer", "arg-store-retailer", "arg-group-manager",
        "ipro-admin", "arg-admin", "arg-support", "arg-category-manager", "arg-accounting"])

    }
  },
  watch: {
    tenant: {
      handler (newValue) {
        if (newValue) {
          this.$store.dispatch('getUserRelatedParties')
          if (newValue === 'pricechopper') {
            this.$store.dispatch('getTenantStores')
          }
        }
      }
    },
    '$auth.isLoading': {
      immediate: true,
      async handler (isLoading) {
        if (isLoading === false && this.$auth.isAuthenticated) {
          this.getUserAttributes()
        }
      }
    }
  },
  mixins: [userAccess, displayAlert],
  created () {
    this.loadPreferences()
  },
  computed: {
    showCreditUpload() {
      return this.$auth.tenant === 'jbg'
    },
    version () {
      return process.env.VUE_APP_VERSION
    },
    maintenanceStatus () {
      return JSON.parse(process.env.VUE_APP_MAINTENANCE_STATUS)
    },
    tenantsWithLogos() {
      return [
        'awg',
        'alliance-retail-group'
      ]
    },
    showAdPlanEdit() {
      const editAttribute = this.userAttributes.find(pa => pa?.attr?.id === "AD_SRP_EDIT_ACCESS")
      return (this.isARGOwner && editAttribute?.value?.toLowerCase() === 'true') || (this.isAdminUser && this.$auth.tenant === 'alliance-retail-group')
    },
    tprSearchLink() {
      if (this.isProd) {
        return 'https://tpr-search.iprosystems.com'
      }
      return 'https://dev-tpr-search.iprosystems.com'
    },
    finSearchLink() {
      if (this.isProd) {
        return 'https://financials-search.iprosystems.com'
      }
      return 'https://dev-financials-search.iprosystems.com'
    },
    tprSearchVisible() {
      if (this.$auth.tenant === 'alliance-retail-group' && this.userRoles.some(element => this.tprSearchRoles.has(element))) {
        return true
      }
      return false
    },
    finSearchVisible() {
      if (this.$auth.tenant === 'alliance-retail-group' && this.userRoles.some(element => this.finSearchRoles.has(element))) {
        return true
      }
      return false
    },
    isProd() {
      return process.env.NODE_ENV === 'production'
    }
  },
  methods: {
    loadPreferences () {
      const savedPreference = localStorage.getItem('preferredFileView')
      if (savedPreference) {
        this.preferredFileView = savedPreference
      } else {
        this.preferredFileView = this.defaultFileView
      }
    },
    handleFilesViewClick (showDefaultView) {
      this.$store.commit('setShowDefaultView', showDefaultView)
      if (this.$route?.path !== '/') {
        this.$router.push('/')
      }
    },
    setDefaultView (newView) {
      localStorage.setItem('preferredFileView', newView)
      this.preferredFileView = newView
    },
    openCreditFormModal () {
      this.showCreditFormModal = true
    },
    logout () {
      this.$auth.logout({
        returnTo: `${window.location.origin}`
      })
      sessionStorage.removeItem('selected_parties')
      sessionStorage.removeItem('selected-tags')
      sessionStorage.removeItem('selected_store')
      sessionStorage.removeItem('selected_group')
      sessionStorage.removeItem('ad_planner')
      sessionStorage.removeItem('ad_planner_party')
      sessionStorage.removeItem('store_id')
      sessionStorage.removeItem('store_name')
    },
    async getUserAttributes() {
      try {
        const customerIdRes = await Party.getCustomerId(this.$store.getters.email)
        if (customerIdRes?.data?.customer_id) {
          const res = await PartyAttributeValue.getAttributeValuesByParty(customerIdRes.data.customer_id, 100, 0);
          
          if (res?.data?.length > 0) {
            this.userAttributes = res.data 
          }
        }
      } catch (err) {
        this.handleError(err)
      } finally {
        this.checkTermConditions()
      }
    },
    async checkTermConditions() {
      if (this.$auth.tenant === 'alliance-retail-group' && this.isARGRetailer) {
        this.termConditionsPA = null
        if (this.userAttributes) {
          this.termConditionsPA = this.userAttributes.find(pa => pa.attr.id === "TERMS_CONDITIONS_ACCEPTED")
        }
        if (!this.termConditionsPA || this.termConditionsPA?.value?.toLowerCase() !== 'true') {
          this.showTermsAndConditionsModal = true
        }
        this.termConditionsChecked = true
        
      } else {
        this.termConditionsChecked = true
      }
    },
    async updateTC() {
      let saveData = null
      const customerIdRes = await Party.getCustomerId(this.$store.getters.email)
      if (this.termConditionsPA) {
        saveData = {
          ...this.termConditionsPA,
          value: 'True'
        }
      } else {
        saveData = {
          attr_id: "TERMS_CONDITIONS_ACCEPTED",
          created_by: this.$store.getters.email,
          party_id: customerIdRes.data.customer_id,
          tenant: 'alliance-retail-group',
          created_on: new Date().toISOString(),
          updated_on: new Date().toISOString(),
          value: 'True'
        }
      }
      await this.handleAttributeValue(saveData)
    },
    async handleAttributeValue (saveData) {
      if (saveData.id && saveData.attr) {
        const saveId = saveData.id
        saveData.attr_id = saveData.attr.id
        saveData.updated_by = this.$store.getters.email
        delete saveData.attr
        delete saveData.id
        await PartyAttributeValue.put(saveId, saveData).then(() => {
          this.emitAlert(true, 'success', 'Your acceptance has been recorded.', [], true)
          this.showTermsAndConditionsModal = false
        }).catch(err => {
          this.handleError(err)
        })
      } else {
        await PartyAttributeValue.post(saveData).then(() => {
          this.emitAlert(true, 'success', 'Your acceptance has been recorded.', [], true)
          this.showTermsAndConditionsModal = false
        }).catch(err => {
          this.handleError(err)
        })
      }
    },
  }
}
</script>
<style lang="scss" scoped>
span.registeredTrademark {
  position: relative;
  top: -.3rem;
  font-size: .5rem;
  left: -0.05rem;
}

footer {
  font-size: small;
}
</style>
